import React, { useCallback, useState } from 'react';
import { Button, ControlLink, Input, LoadingBalls, Overlay, Stack, Text } from '@moda/om';
import { noop, useForm } from '@moda/portal-stanchions';
import { AuthAction, AuthPayloadSuccess } from '../../lib/authenticate';
import { usePasswordlessAuth } from '../../hooks/usePasswordlessAuth';
import { SocialAuthButtons } from './SocialAuthButtons';

type PasswordlessRequestFormValues = {
  signInEmail: string;
};

const VALIDATIONS = {
  signInEmail: {
    email: {
      message: 'Invalid email address.',
      value: true
    },
    required: {
      message: 'Please enter an email address.',
      value: true
    }
  }
};

type RequestStepProps = {
  defaultEmail?: string;
  disableEmail?: boolean;
  fieldsFooter?: React.ReactNode;
  hideAuthModal?: () => void;
  message?: string;
  onAuth: (authUser?: AuthPayloadSuccess) => void;
  onRequested: ({ challengeId, email }: { challengeId: string; email: string }) => void;
  showAmazonMessage?: boolean;
  showAuthModal?: () => void;
  showCancelButton?: boolean;
  showSocialAuth?: boolean;
  showPrivacyPolicy?: boolean;
  title?: string;
};

export const PasswordlessRequestStep: React.FC<RequestStepProps> = ({
  defaultEmail,
  disableEmail = false,
  fieldsFooter,
  hideAuthModal,
  message = "Enter your email and we'll send you a code to sign in",
  onAuth,
  onRequested,
  showAmazonMessage,
  showAuthModal,
  showCancelButton = true,
  showSocialAuth = true,
  showPrivacyPolicy = false,
  title = 'Sign In or Create an Account'
}) => {
  const { clearError, error, loading, requestLogin } = usePasswordlessAuth();
  const [email, setEmail] = useState(defaultEmail || '');

  const handleEmailChange = useCallback(
    (value: string | number) => {
      if (error) clearError();
      setEmail(String(value));
    },
    [clearError, error]
  );

  const handleRequestSubmit = useCallback(
    (values: PasswordlessRequestFormValues) => {
      const trimmedEmail = values.signInEmail.trim();

      requestLogin({ email: trimmedEmail })
        .then(result => {
          onRequested({ challengeId: result.challengeId, email: trimmedEmail });

          return result;
        })
        .catch(noop);
    },
    [onRequested, requestLogin]
  );

  const { errors: fieldErrors, handleSubmit } = useForm({
    onSubmit: handleRequestSubmit,
    validations: VALIDATIONS
  });

  return (
    <>
      <form onSubmit={handleSubmit} data-testid="signInSection">
        {title && <div className="Authentication__title">{title}</div>}
        {message && <div className="Authentication__message">{message}</div>}
        {error?.message && (
          <div className="Authentication__error" role="alert">
            {error.message}
          </div>
        )}

        <Stack space={5}>
          <div className="Authentication__fields">
            <Input
              className="Authentication__input"
              disabled={disableEmail}
              error={fieldErrors.signInEmail?.message || error != null}
              id="signInEmail"
              data-testid="signInEmail"
              onChange={handleEmailChange}
              placeholder="Email"
              type="email"
              value={email}
            />
          </div>

          {fieldsFooter}

          <Stack space={2}>
            <Button
              className="Authentication__button Authentication__button--sign-in"
              type="submit"
              data-testid="signInSubmit"
            >
              CONTINUE
            </Button>

            {showCancelButton && hideAuthModal && (
              <Button
                className="Authentication__button Authentication__button--cancel-sign-in"
                onClick={hideAuthModal}
                secondary
                type="button"
              >
                Cancel
              </Button>
            )}
          </Stack>
        </Stack>
      </form>

      {showSocialAuth && (
        <>
          <div className="Authentication__divider" />

          <SocialAuthButtons
            action={AuthAction.SignIn}
            theme="black-and-white"
            showAmazonMessage={showAmazonMessage ?? true}
            showAuthModal={showAuthModal}
            hideAuthModal={hideAuthModal}
            onAuth={onAuth}
          />
        </>
      )}

      {showPrivacyPolicy && (
        <div className="Authentication__privacy-policy">
          <ControlLink to="/privacy" rel="noopener noreferrer" target="_blank" underlined>
            Privacy Policy
          </ControlLink>
        </div>
      )}

      <Overlay className="Authentication__overlay" show={loading}>
        <LoadingBalls />

        <Text treatment="eyebrow">Loading</Text>
      </Overlay>
    </>
  );
};
